import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatIDR } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { RiskAlert } from '../components/RiskAlert';
import { StatusBadge } from '../components/StatusBadge';
import { Check, Copy, Upload, ArrowLeft } from 'lucide-react';

export default function Membership() {
  const { membership, isActive, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  const load = async () => {
    const [i, p] = await Promise.all([api.get('/public/info'), api.get('/payments/me')]);
    setInfo(i.data);
    const pend = (p.data.payments || []).find(x => ['PENDING','SUBMITTED'].includes(x.status));
    setPayment(pend || (p.data.payments || [])[0] || null);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (isActive) navigate('/dashboard'); }, [isActive]);

  const createPayment = async () => {
    setLoading(true);
    try { const { data } = await api.post('/payments/create', { payment_method: 'MANUAL_BANK_TRANSFER' }); setPayment(data.payment); }
    catch (e) { toast.error('Gagal membuat order'); } finally { setLoading(false); }
  };

  const uploadProof = async () => {
    if (!file) return toast.error('Pilih file bukti transfer');
    setLoading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      await api.post(`/payments/${payment.payment_id}/proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Bukti terkirim. Menunggu verifikasi admin.');
      await load(); await refreshMe();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal upload'); } finally { setLoading(false); }
  };

  const price = info?.membership_price || 500000;
  const inst = payment?.instructions;

  return (
    <div className="min-h-screen bg-secondary/30 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="mb-4"><ArrowLeft className="h-4 w-4 mr-1" />Dashboard</Button>
        <h1 className="font-display text-3xl font-bold">Aktivasi Membership</h1>
        <p className="text-muted-foreground mt-1">Gold Trading Academy — {formatIDR(price)}/tahun</p>

        {membership?.status === 'PENDING' && !payment && (
          <Card className="p-6 mt-6">
            <h2 className="font-semibold text-lg">Pilih Membership</h2>
            <div className="mt-4 rounded-xl border p-5 flex items-center justify-between">
              <div><div className="font-display text-2xl font-bold">{formatIDR(price)}<span className="text-sm text-muted-foreground font-normal">/tahun</span></div><div className="text-sm text-muted-foreground">Akses penuh 365 hari</div></div>
              <Button onClick={createPayment} disabled={loading} className="bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))]" data-testid="choose-membership-button">Lanjut Bayar</Button>
            </div>
          </Card>
        )}

        {payment && (
          <>
            <Card className="p-6 mt-6">
              <div className="flex items-center justify-between"><h2 className="font-semibold text-lg">Instruksi Pembayaran</h2><StatusBadge status={payment.status} testid="payment-status-badge" /></div>
              {inst && (
                <div className="mt-4 space-y-2 text-sm">
                  <Row label="Bank" value={inst.bank_name} />
                  <Row label="No. Rekening" value={inst.account_number} copy />
                  <Row label="Atas Nama" value={inst.account_holder} />
                  <Row label="Jumlah" value={formatIDR(inst.amount)} copy copyVal={String(inst.amount)} />
                  <Row label="Kode / Berita" value={payment.reference_number} copy />
                  <p className="text-xs text-muted-foreground pt-2">{inst.note}</p>
                </div>
              )}
            </Card>

            {payment.status !== 'APPROVED' && (
              <Card className="p-6 mt-6">
                <h2 className="font-semibold text-lg">Upload Bukti Transfer</h2>
                <p className="text-sm text-muted-foreground mt-1">Format JPG/PNG/PDF, maks 5MB.</p>
                <div className="mt-4">
                  <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 cursor-pointer hover:bg-muted/40 transition-colors">
                    <Upload className="h-6 w-6 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{file ? file.name : 'Klik untuk pilih file'}</span>
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={e => setFile(e.target.files[0])} data-testid="proof-upload-input" />
                  </label>
                </div>
                <div className="mt-4"><RiskAlert compact /></div>
                <Button onClick={uploadProof} disabled={loading || !file} className="w-full mt-4 h-11 bg-[hsl(var(--brand-ink))] text-white" data-testid="proof-submit-button">{loading ? 'Mengunggah...' : 'Kirim Bukti Transfer'}</Button>
                {payment.status === 'SUBMITTED' && <p className="text-sm text-center text-sky-700 mt-3">Bukti sedang ditinjau admin (maks 1x24 jam kerja).</p>}
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, copy, copyVal }) {
  return (
    <div className="flex items-center justify-between border-b py-2 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-2 font-medium tabular-nums">{value}
        {copy && <button onClick={() => { navigator.clipboard.writeText(copyVal || value); toast.success('Disalin'); }} className="text-muted-foreground hover:text-foreground"><Copy className="h-3.5 w-3.5" /></button>}
      </span>
    </div>
  );
}
